import Styles from './MultiCaseStudyForm.module.scss'
import { useState, useMemo, useEffect, useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { nanoid } from 'nanoid';
import {
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    Delete as DeleteIcon,
    KeyboardArrowLeft,
    Preview,
    Publish,
    Save,
    Info,
    Close,
    DragIndicator,
    IndeterminateCheckBoxOutlined,
    AddToPhotos,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { CaseStudyForm } from './CreateCaseStudy';
import { useContext } from 'react';
import { ViewContext } from 'components/lib';
import axios from 'axios';
import { UserCaseStudy } from '../DIYQuiz/UserCaseStudy';

// DnD item type constant
const CASE_STUDY_ITEM = 'case-study';

// Draggable case study component
const DraggableCaseStudy = ({
    caseStudy,
    index,
    expandedPanels,
    setExpandedPanels,
    handleCaseStudyChange,
    handleRequiredToggle,
    handleVideoToggle,
    handleDeleteCaseStudy,
    moveItem,
    totalItems,
}) => {
    const ref = useRef(null);
    const dragHandleRef = useRef(null);

    // Drag functionality
    const [{ isDragging }, drag, preview] = useDrag({
        type: CASE_STUDY_ITEM,
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    // Drop functionality
    const [, drop] = useDrop({
        accept: CASE_STUDY_ITEM,
        hover: (item, monitor) => {
            if (!ref.current) {
                return;
            }

            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            moveItem(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    // Apply preview ref to the whole item but drag ref to handle
    preview(drop(ref)); // Apply preview to the whole item
    drag(dragHandleRef); // Only apply drag to the handle

    const handleAccordianExpand = () => {
        setExpandedPanels((prev) =>
            prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
        );
    };

    return (
        <div
            ref={ref}
            className={`${Styles.draggableCaseStudy} ${isDragging ? Styles.dragging : ''}`}
        >
            <Accordion
                expanded={expandedPanels.includes(index)}
                onChange={handleAccordianExpand}
                className={Styles.caseStudyAccordion}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={Styles.accordionSummary}
                >
                    <div className={Styles.accordionHeader}>
                        <div ref={dragHandleRef} className={Styles.dragHandle} >
                            <DragIndicator fontSize="small" />
                        </div>
                        <TextField
                            variant="filled"
                            autoFocus
                            required
                            value={caseStudy.name}
                            label="Case Study Name"
                            sx={{
                                width: '300px',
                                '& .MuiFilledInput-root': {
                                    backgroundColor: '#f9f9f9',
                                },
                            }}
                            onChange={(e) => handleCaseStudyChange(index, { name: e.target.value })}
                            className={Styles.caseStudyTitleInput}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                        />

                        <div className={Styles.accordionActions}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={caseStudy.is_required || false}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleRequiredToggle(index);
                                        }}
                                        color="primary"
                                    />
                                }
                                label="Required"
                                onClick={(e) => e.stopPropagation()}
                                className={Styles.requiredCheckbox}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={caseStudy.is_video_required || false}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleVideoToggle(index);
                                        }}
                                        color="primary"
                                    />
                                }
                                label="Video"
                                onClick={(e) => e.stopPropagation()}
                                className={Styles.requiredCheckbox}
                            />

                            {totalItems > 1 && (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCaseStudy(index);
                                    }}
                                    sx={{ color: 'red' }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </div>
                    </div>
                </AccordionSummary>
                <AccordionDetails className={Styles.caseStudyForm}>
                    <CaseStudyForm
                        caseStudyData={caseStudy}
                        onChange={(updatedData) => handleCaseStudyChange(index, updatedData)}
                        isSubComponent={true}
                    />
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export function MultiCaseStudyForm({ initialData }) {
    const viewContext = useContext(ViewContext)
    const navigate = useNavigate();
    const { diyQuizId, assessmentId } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type');

    // Function to create an empty case study with a unique ID
    function createEmptyCaseStudy(index = 0) {
        return {
            id: nanoid(),
            name: `Case Study ${index + 1}`,
            scenario: '',
            question: '',
            response_template: {
                ideal: '',
                average: '',
                poor: ''
            },
            criteria: [],
            is_required: false,
            is_video_required: false
        };
    }

    // Main quiz state containing overall quiz details and case studies array
    const [quiz, setQuiz] = useState({
        name: '',
        description: '',
        type: type,
        questions: [createEmptyCaseStudy()],
        status: 'draft',
        minimum_to_complete: 1,
    });

    const [initialFormData, setInitialFormData] = useState(null);
    const [isPublishable, setIsPublishable] = useState(false);
    const [newAssessmentId, setNewAssessmentId] = useState('')
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [expandedPanels, setExpandedPanels] = useState([]);
    const [touched, setTouched] = useState({
        name: false,
        description: false,
        minimum_to_complete: false
    });

    // Initialize form data from initialData prop
    useEffect(() => {
        if (initialData) {
            // Format data if needed for edit mode
            const formattedData = {
                ...initialData,
                questions: initialData.questions?.map(q => ({
                    ...q,
                    // Ensure each existing question has an ID
                    id: q.id || nanoid(),
                    is_required: q.is_required || false,
                    is_video_required: q.is_video_required || false
                })) || [createEmptyCaseStudy()],
                minimum_to_complete: initialData.minimum_to_complete || 1
            };

            setQuiz(formattedData);
            setInitialFormData(formattedData);

            if (initialData.status === 'draft') {
                setIsPublishable(true);
            }
        } else {
            // If no initialData, use the default state
            setInitialFormData(quiz);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    // Count required case studies and update minimum_to_complete if needed
    useEffect(() => {
        const requiredCount = quiz.questions.filter(q => q.is_required).length;

        if (requiredCount > quiz.minimum_to_complete) {
            setQuiz(prev => ({
                ...prev,
                minimum_to_complete: requiredCount
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quiz.questions]);

    const hasUnsavedChanges = useMemo(() => {
        return JSON.stringify(quiz) !== JSON.stringify(initialFormData);
    }, [quiz, initialFormData]);

    const handleAddCaseStudy = () => {
        setQuiz(prev => {
            const newIndex = prev.questions.length;
            const newCaseStudy = createEmptyCaseStudy(newIndex);
            return {
                ...prev,
                questions: [...prev.questions, newCaseStudy]
            };
        });

        setExpandedPanels(prev => [...prev, prev.length]); // Ensure correct index is added
    };

    const handleCaseStudyChange = (index, updatedFields) => {
        setQuiz(prev => ({
            ...prev,
            questions: prev.questions.map((cs, i) =>
                i === index ? { ...cs, ...updatedFields } : cs
            )
        }));
    };

    const handleRequiredToggle = (index) => {
        setQuiz(prev => ({
            ...prev,
            questions: prev.questions.map((cs, i) =>
                i === index ? { ...cs, is_required: !cs.is_required } : cs
            )
        }));
    };

    const handleVideoToggle = (index) => {
        setQuiz(prev => ({
            ...prev,
            questions: prev.questions.map((cs, i) =>
                i === index ? { ...cs, is_video_required: !cs.is_video_required } : cs
            )
        }));
    };

    const handleDeleteCaseStudy = (index) => {
        if (quiz.questions.length > 1) {
            setQuiz(prev => {
                const newQuestions = prev.questions.filter((_, i) => i !== index);
                // const requiredCount = newQuestions.filter(q => q.is_required).length;
                // const minRequired = Math.max(requiredCount, 1);

                return {
                    ...prev,
                    questions: newQuestions,
                    minimum_to_complete: Math.min(prev.minimum_to_complete, newQuestions.length)
                };
            });
        }
    };

    // Handle moving case studies (for drag and drop)
    const moveItem = (dragIndex, hoverIndex) => {
        setQuiz(prev => {
            const newQuestions = [...prev.questions];
            const [draggedItem] = newQuestions.splice(dragIndex, 1);
            newQuestions.splice(hoverIndex, 0, draggedItem);

            return {
                ...prev,
                questions: newQuestions
            };
        });

        setExpandedPanels(prev =>
            prev.map(i => (i === dragIndex ? hoverIndex : i === hoverIndex ? dragIndex : i))
        );
    };

    const handleQuizDetailChange = (e) => {
        const { name, value } = e.target;

        if (name === 'minimum_to_complete') {
            const requiredCount = quiz.questions.filter(q => q.is_required).length;
            const minValue = Math.max(requiredCount, 1);
            const maxValue = quiz.questions.length;

            // Ensure the value is within the valid range
            const numValue = parseInt(value) || minValue;
            const clampedValue = Math.min(Math.max(numValue, minValue), maxValue);

            setQuiz(prev => ({
                ...prev,
                [name]: clampedValue
            }));
        } else {
            setQuiz(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleBlur = (fieldName) => {
        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // Calculate minimum and maximum values for minimum_to_complete
    const requiredCount = useMemo(() =>
        quiz.questions.filter(q => q.is_required).length,
        [quiz.questions]);

    const minRequiredValue = Math.max(requiredCount, 1);
    const maxRequiredValue = quiz.questions.length;

    // Error validation for basic fields
    const errors = useMemo(() => ({
        name: touched.name && !quiz?.name?.trim() ? 'Name is required' : '',
        description: touched.description && !quiz?.description?.trim() ? 'Description is required' : '',
        minimum_to_complete: touched.minimum_to_complete &&
            (quiz.minimum_to_complete < minRequiredValue ||
                quiz.minimum_to_complete > maxRequiredValue)
            ? `Value must be between ${minRequiredValue} and ${maxRequiredValue}` : '',
    }), [quiz, touched, minRequiredValue, maxRequiredValue]);

    const isFormValid = useMemo(() => {
        return (
            quiz.name?.trim() !== '' &&
            quiz?.description?.trim() !== '' &&
            quiz.minimum_to_complete >= minRequiredValue &&
            quiz.minimum_to_complete <= maxRequiredValue &&
            quiz.questions.every(caseStudy =>
                caseStudy.scenario?.trim() !== '' &&
                caseStudy.question?.trim() !== '' &&
                caseStudy.response_template.ideal?.trim() !== '' &&
                caseStudy.response_template.average?.trim() !== '' &&
                caseStudy.response_template.poor?.trim() !== '' &&
                caseStudy.criteria.length > 0 &&
                Math.abs(caseStudy.criteria.reduce((sum, c) => sum + parseFloat(c.weightage || 0), 0) - 100) < 0.01
            )
        );
    }, [quiz, minRequiredValue, maxRequiredValue]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!isFormValid) {
            // Mark fields as touched to show errors
            setTouched({
                name: true,
                description: true,
                minimum_to_complete: true
            });
            return;
        }

        try {
            const updateAssessmentId = assessmentId || newAssessmentId;
            const url = updateAssessmentId
                ? `/api/diy-quiz/${diyQuizId}/assessments/${updateAssessmentId}`
                : `/api/diy-quiz/${diyQuizId}/assessments`;
            const method = updateAssessmentId ? 'patch' : 'post';

            // Extract only the needed fields for submission
            const { type: quizType, currentKeyword, time_limit, status, ai_response, ...restData } = quiz;

            // Convert time_limit from "MM:SS" format to seconds if present
            const convertTimeToSeconds = (time) => {
                if (typeof time === 'string' && time.includes(':')) {
                    const [minutes, seconds] = time.split(':').map(Number);
                    return minutes * 60 + seconds;
                }
                return time;
            };

            const submissionData = {
                ...restData,
                ...(time_limit ? { time_limit: convertTimeToSeconds(time_limit) } : {}),
                ...(!updateAssessmentId && { type: quizType })
            };

            const response = await axios[method](url, submissionData, {
                headers: {
                    accept: 'application/json',
                },
            });

            if (response.data) {
                const message = updateAssessmentId
                    ? 'Updated Case Study Successfully'
                    : 'Created Case Study Successfully';

                console.log(message);
                viewContext.notification.show(message, 'success', true);
                setIsPublishable(true);

                if (!updateAssessmentId) {
                    setNewAssessmentId(response.data.data.id);
                }

                // Update initialFormData to match current state
                setInitialFormData(quiz);
            } else {
                viewContext.notification.show(
                    'Failed to save case study',
                    'error',
                    true
                );
            }
        } catch (error) {
            console.error("Error saving case study:", error);
            viewContext.notification.show(
                'Failed to save case study',
                'error',
                true
            );
        }
    };

    const handleBack = () => {
        if (hasUnsavedChanges) {
            setShowConfirmDialog(true);
        } else {
            navigate(`/diy-quiz/${diyQuizId}/assessments`);
        }
    };

    const handlePublish = () => {
        const data = {
            status: 'published'
        };

        const updateAssessmentId = assessmentId || newAssessmentId;
        const url = `/api/diy-quiz/${diyQuizId}/assessments/${updateAssessmentId}`;

        axios.patch(
            url,
            data,
            {
                headers: {
                    accept: 'application/json',
                }
            }
        )
            .then(response => {
                if (response.data) {
                    viewContext.notification.show('Successfully published assessment', 'success', true);
                    navigate(`/diy-quiz/${diyQuizId}/assessments`);
                } else {
                    viewContext.notification.show('Error publishing assessment', 'error', true);
                }
            })
            .catch(error => {
                console.error('Error:', error);
                viewContext.notification.show('Error publishing assessment', 'error', true);
            });
    };

    return (
        <div className={Styles.ancestorContainer}>
            {/* Header Section */}
            <div className={Styles.headerSection}>
                <Button
                    variant="text"
                    startIcon={<KeyboardArrowLeft />}
                    onClick={handleBack}
                    className={Styles.backButton}
                >
                    Back
                </Button>
                <Typography variant="h4" className={Styles.pageTitle}>
                    {assessmentId ? 'Edit Assessment' : 'Create Assessment'}
                </Typography>
            </div>

            <Dialog
                open={showPreviewDialog}
                onClose={() => setShowPreviewDialog(false)}
                PaperProps={{
                    sx: {
                        width: '80%',
                        maxWidth: 'none',
                        margin: 'auto'
                    }
                }}
                fullWidth
                maxWidth={false}
            >
                <DialogTitle sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <span>
                        Case Study Preview
                    </span>
                    <span>
                        <IconButton onClick={() => { setShowPreviewDialog(false) }}>
                            <Close fontSize='small' />
                        </IconButton>
                    </span>
                </DialogTitle>
                <DialogContent>
                    <UserCaseStudy data={quiz} viewOnly={true} />
                </DialogContent>
            </Dialog>

            <div className={Styles.caseStudyCreate}>
                {/* Quiz Details Card */}
                <Card className={Styles.quizDetailsCard}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Assessment Details
                        </Typography>
                        <div className={Styles.formFields}>
                            <TextField
                                fullWidth
                                variant='filled'
                                required
                                label="Assessment Name"
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                                name="name"
                                value={quiz.name}
                                onChange={handleQuizDetailChange}
                                onBlur={() => handleBlur('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                className={Styles.formField}
                            />
                            <TextField
                                fullWidth
                                required
                                variant='filled'
                                label="Assessment Description"
                                name="description"
                                value={quiz.description}
                                onChange={handleQuizDetailChange}
                                onBlur={() => handleBlur('description')}
                                error={!!errors.description}
                                helperText={errors.description}
                                multiline
                                rows={3}
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                                className={Styles.formField}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Case Studies Section with Drag and Drop */}
                <div className={Styles.caseStudiesSection}>
                    <div style={{ display: "flex", width: "100%", justifyContent: 'flex-end', alignItems: 'center', gap: '1em' }}>
                        <Button
                            startIcon={expandedPanels.length ? <IndeterminateCheckBoxOutlined fontSize="small" /> : <AddToPhotos fontSize='small' />}
                            sx={{

                            }}
                            onClick={() => { setExpandedPanels(prev => prev.length ? [] : [...Array(quiz.questions.length).keys()]); }}
                        >
                            {expandedPanels.length ? 'Collapse All' : 'Expand All'}
                        </Button>
                    </div>
                    <DndProvider backend={HTML5Backend}>
                        {quiz.questions.map((caseStudy, index) => (
                            <DraggableCaseStudy
                                key={caseStudy.id}
                                index={index}
                                caseStudy={caseStudy}
                                expandedPanels={expandedPanels}
                                setExpandedPanels={setExpandedPanels}
                                handleCaseStudyChange={handleCaseStudyChange}
                                handleVideoToggle={handleVideoToggle}
                                handleRequiredToggle={handleRequiredToggle}
                                handleDeleteCaseStudy={handleDeleteCaseStudy}
                                moveItem={moveItem}
                                totalItems={quiz.questions.length}
                            />
                        ))}
                    </DndProvider>
                </div>

                {/* Add Case Study Button */}
                <div id="addButtonContainer" className={Styles.addButtonContainer}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddCaseStudy}
                        className={Styles.addCaseStudyButton}
                    >
                        Add Case Study
                    </Button>
                </div>

                {/* Minimum Required Case Studies Card */}
                <Card className={Styles.minRequiredCard}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Completion Requirements
                        </Typography>
                        <div className={Styles.minRequiredFieldContainer}>
                            <div className={Styles.minRequiredField}>
                                <span>
                                    Minimum Case Studies to be completed by the students to submit the assessment
                                </span>
                                <TextField
                                    variant="filled"
                                    required
                                    type="number"
                                    label="Minimum case studies to complete"
                                    name="minimum_to_complete"
                                    value={quiz.minimum_to_complete}
                                    onChange={handleQuizDetailChange}
                                    onBlur={() => handleBlur('minimum_to_complete')}
                                    error={!!errors.minimum_to_complete}
                                    helperText={errors.minimum_to_complete}
                                    InputProps={{
                                        inputProps: {
                                            min: minRequiredValue,
                                            max: maxRequiredValue
                                        }
                                    }}
                                    sx={{
                                        width: '250px',
                                        '& .MuiFilledInput-root': {
                                            backgroundColor: '#f9f9f9',
                                        },
                                    }}
                                />
                            </div>
                            <Typography variant="body2" className={Styles.helpText} sx={{ mt: 1 }}>
                                <Info fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                                {requiredCount > 0 ?
                                    `This includes ${requiredCount} required case ${requiredCount === 1 ? 'study' : 'studies'}.` :
                                    'No case studies are marked as required.'}
                                {' '}Minimum value is {minRequiredValue} and maximum is {maxRequiredValue}.
                            </Typography>
                        </div>
                    </CardContent>
                </Card>

                {/* Required Message */}
                <div className={Styles.requiredMessage}>
                    All fields marked * are required
                </div>

                {/* Action Buttons */}
                <div className={Styles.actionButtons}>
                    <AivagamButton
                        buttonText={
                            <span className={Styles.submitButton}>
                                <Save fontSize="small" />Save
                            </span>
                        }
                        type="confirm"
                        onClick={handleSave}
                        icon={true}
                        disabled={!isFormValid || !hasUnsavedChanges}
                        classStyles={{
                            width: '8em',
                            height: '3em',
                            borderRadius: '8px',
                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
                        }}
                    />
                    <AivagamButton
                        buttonText={
                            <span className={Styles.submitButton}>
                                <Preview fontSize="small" />Preview
                            </span>
                        }
                        type="action"
                        icon={true}
                        onClick={() => { setShowPreviewDialog(true) }}
                        disabled={!isFormValid}
                        classStyles={{
                            width: '8em',
                            height: '3em',
                            borderRadius: '8px',
                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
                        }}
                    />
                    <AivagamButton
                        buttonText={
                            <span className={Styles.submitButton}>
                                <Publish fontSize="small" />Publish
                            </span>
                        }
                        onClick={handlePublish}
                        disabled={!isFormValid || (quiz?.status && quiz?.status !== 'draft') || !isPublishable}
                        icon={true}
                        classStyles={{
                            width: '8em',
                            height: '3em',
                            borderRadius: '8px',
                            boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
                        }}
                    />
                </div>

                <div className={Styles.footerSection}>
                    <Info className={Styles.footerIcon} />
                    <Typography variant="body2" className={Styles.footerText}>
                        The case study must be saved before it can be published. Once published, it can be sent to users.
                    </Typography>
                </div>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have unsaved changes. Are you sure you want to leave?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmDialog(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => navigate(`/diy-quiz/${diyQuizId}/assessments`)} color="error">
                        Leave
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}